import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Spin, message } from 'antd';
import axios from 'axios';

import ChatBox from './ChatBox';
import useScroll from '../../hooks/useScroll';
import { API_CHATBOT_BASE_URL } from '../../utility';
import { getServiceUrl } from '../../config';

const API_URL = getServiceUrl();

function ChatBot(props) {
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chat, setChat] = useState([]);
  const [choices, setChoices] = useState([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const { patientguid } = useParams();
  const scrollBottom = useScroll(chat);

  useEffect(() => {
    const startAssessment = async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(
          `${API_CHATBOT_BASE_URL}/start_assessment`,
          {
            user_id: patientguid,
            child_age_months: 5,
          }
        );
        console.log('✅ /start_assessment response:', data);
        setConversationId(data.conversation_id);
        setChoices(data.choices);
        setChat((prevChat) => [
          ...prevChat,
          { msg: data.question, sent: false },
        ]);
      } catch (error) {
        console.error('❌ Error starting assessment:', error);
      } finally {
        setLoading(false);
      } 
    };

    startAssessment();
  }, [patientguid]);

  const sendResultToBackend = async (resultData) => {
    try {
      console.log('📤 Final result data to send:', resultData);
      await axios.post(`${API_URL}patient/chatbot/save`, resultData, {
        headers: {
          patientguid,
          rdxtoken: localStorage.getItem('tokenId'),
        },
      });
      console.log('✅ Saved result to patient/chatbot/save');

      const hpoIdArray = resultData['HPO Terms'].map((term) => {
        const hpoNumber = term.split(':')[1].slice(0, -1);
        const hpoId = `HP:${hpoNumber}`;
        return {
          hpoid: hpoId,
          source: 'Assessment',
          enddata: null,
          startdate: null,
          sourcefile: null,
        };
      });

      console.log('📎 Extracted HPO IDs:', hpoIdArray);

      await axios.post(`${API_URL}doctors/savepatienthpoids`, {
        patientguid,
        hpoids: hpoIdArray,
      });

      console.log('✅ Saved HPO terms to doctors/savepatienthpoids');
    } catch (error) {
      console.error('❌ Error sending result to backend:', error);
    }
  };

  const sendMessage = async (userMessage) => {
    console.log('🗣️ User selected:', userMessage);
    setChat((prevChat) => [...prevChat, { msg: userMessage, sent: true }]);
    setIsSendingMessage(true);

    const timerId = setTimeout(() => {
      setShowFullScreenLoader(true);
    }, 5000);

    try {
      const { data } = await axios.post(`${API_CHATBOT_BASE_URL}/chatbot`, {
        conversation_id: conversationId,
        message: userMessage,
      });

      console.log('🤖 Chatbot response:', data);
      console.log('📋 Choices received:', data.choices);

      setChat((prevChat) => [...prevChat, { msg: data.response, sent: false }]);
      setChoices(data.choices || []);

      if (data['Total Risk Score'] || data['Summary']) {
        console.log('🧠 Final risk data detected. Processing...');
        setFinalizing(true);
        const hideLoading = message.loading('Finalizing assessment...', 0);
        await sendResultToBackend(data);
        hideLoading();
        setFinalizing(false);
        
        // Move to the results page instead of patient profile
        props.setCurrStep(3); // Move to ChatBotResult component
      }
    } catch (error) {
      console.error('❌ Error sending message to chatbot:', error);
    } finally {
      clearTimeout(timerId);
      setShowFullScreenLoader(false);
      setIsSendingMessage(false);
    }
  };

  return (
    <div
      ref={scrollBottom}
      className="relative m-auto flex h-full w-4/5 flex-col overflow-y-auto rounded-xl bg-slate-100"
    >
      {/* Chat Messages */}
      <div className="flex-grow p-4">
        {chat.map((msg, index) => (
          <ChatBox key={index} msg={msg} />
        ))}
      </div>

      {/* Choices */}
      <div className="sticky bottom-0 flex flex-wrap justify-end gap-5 bg-gradient-to-t from-slate-200 via-slate-100 to-transparent p-6">
        {choices.map((choice, index) => {
          const label = typeof choice === 'string' ? choice : choice.label;
          return (
            <Button
              key={index}
              onClick={() => sendMessage(label)}
              disabled={loading || isSendingMessage || finalizing}
              loading={isSendingMessage || finalizing}
              size="large"
            >
              {label}
            </Button>
          );
        })}
      </div>

      {/* Full-Screen Loader Overlay */}
      {showFullScreenLoader && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin size="large" tip="Computing result..." />
        </div>
      )}
    </div>
  );
}

export default ChatBot;
